import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { RentalPortalPhaseBar } from '../../RentalPortalPhaseBar'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import {
  APPLICANT_PROFILE_FOLLOW_UP_ACTION_KEY_DELETE,
  ApplicantProfileFollowUpItem,
} from './ApplicantProfileFollowUpItem'
import { ApplicantProfileHeaderInfoItemsSkeleton } from './ApplicantProfileHeaderInfoItemsSkeleton'
import { ApplicantProfilePortalReceivedItem } from './ApplicantProfilePortalReceivedItem'
import { ApplicantProfileResponsibleManagerItem } from './ApplicantProfileResponsibleManagerItem'
import {
  APPLICANT_PROFILE_HEADER_INFO_ITEMS_COLUMNS,
  APPLICANT_PROFILE_INFO_ITEM_TITLE_KEY,
  QA_NAME_APPLICANT_PROFILE_PHASE_BAR,
} from './RentalPortalApplicantProfileHeaderInfoItems.constants'
import { useApplicantProfileHeaderInfoItems } from './RentalPortalApplicantProfileHeaderInfoItems.hooks'
import {
  getApplicantPhaseItems,
  getApplicantPhaseTerminalStatement,
  getCanEditApplicantFollowUp,
  getCanEditApplicantResponsibleManager,
  getIsApplicantTerminal,
} from './RentalPortalApplicantProfileHeaderInfoItems.utils'

import * as styles from './RentalPortalApplicantProfileHeaderInfoItems.module.css'

function RentalPortalApplicantProfileHeaderInfoItems({ isLoading }) {
  const { t } = useTranslation()

  const { applicantData, isPreview } = useApplicantProfileContext()

  const { handleOpenResponsibleManagerModal, handleSaveFollowUp } =
    useApplicantProfileHeaderInfoItems()

  const isPhaseTerminal = Boolean(applicantData?.phase?.is_terminal)

  const phaseItems = getApplicantPhaseItems({
    isTerminal: isPhaseTerminal,
    phases: applicantData?.phases,
    t,
  })

  const canEditFollowUp = getCanEditApplicantFollowUp({
    applicantData,
    isPreview,
  })

  const canEditResponsibleManager = getCanEditApplicantResponsibleManager({
    applicantData,
    isPreview,
  })

  return (
    <div className={styles.RentalPortalApplicantProfileHeaderInfoItems}>
      <RentalPortalPhaseBar
        isLoading={isLoading}
        items={phaseItems}
        qaName={QA_NAME_APPLICANT_PROFILE_PHASE_BAR}
        terminalStatement={
          isPhaseTerminal
            ? getApplicantPhaseTerminalStatement({
                phase: applicantData?.phase,
                t,
                terminalDate: applicantData?.terminal_at,
              })
            : null
        }
      />
      <div
        className={styles.infoGrid}
        style={{
          '--applicant-profile-header-info-items-columns':
            APPLICANT_PROFILE_HEADER_INFO_ITEMS_COLUMNS,
        }}
      >
        {isLoading ? (
          <ApplicantProfileHeaderInfoItemsSkeleton
            className={styles.infoItem}
          />
        ) : (
          <>
            <ApplicantProfileFollowUpItem
              canEdit={canEditFollowUp}
              className={styles.infoItem}
              contentWrapperClassName={classNames(styles.contentWrapper, {
                [styles.editableContentWrapper]: canEditFollowUp,
              })}
              isTerminal={getIsApplicantTerminal(applicantData)}
              itemActionsProps={{
                actionsWrapperClassName: classNames(styles.managerActions, {
                  [styles.visibleActions]: !applicantData?.follow_up,
                }),
                buttonsProps: {
                  [APPLICANT_PROFILE_FOLLOW_UP_ACTION_KEY_DELETE]: {
                    className: styles.managerActionButton,
                  },
                },
              }}
              title={APPLICANT_PROFILE_INFO_ITEM_TITLE_KEY.followUp}
              titleClassName={styles.infoItemTitle}
              value={applicantData?.follow_up}
              onSaveValue={handleSaveFollowUp}
            />
            <ApplicantProfileResponsibleManagerItem
              actionButtonClassName={styles.managerActionButton}
              actionsWrapperClassName={styles.managerActions}
              canEdit={canEditResponsibleManager}
              className={styles.infoItem}
              contentWrapperClassName={classNames(styles.contentWrapper, {
                [styles.editableContentWrapper]: canEditResponsibleManager,
              })}
              managerClassName={styles.managerItem}
              titleClassName={styles.infoItemTitle}
              value={applicantData?.responsible_manager}
              onOpenModal={handleOpenResponsibleManagerModal}
            />
            <ApplicantProfilePortalReceivedItem
              applicant={applicantData}
              className={styles.infoItem}
              contentWrapperClassName={styles.contentWrapper}
              titleClassName={styles.infoItemTitle}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default RentalPortalApplicantProfileHeaderInfoItems
